import { Inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Channel, getChannels, getUserHistoryEntries, markAsVisited } from '@planone/bau365/data-access-user';
import { BehaviorSubject, catchError, filter, map, of, switchMap, take, withLatestFrom } from 'rxjs';

import {
  keepContent,
  loadContent,
  loadContentSuccess,
  loadMoreContent,
  loadMoreContentSuccess,
  loadMoreFromRootChannel,
  loadMoreFromRootChannelFailure,
  loadMoreFromRootChannelSuccess,
  loadMoreFromSubscribedChannel,
  loadMoreFromSubscribedChannelFailure,
  loadMoreFromSubscribedChannelSuccess,
  loadMoreFromUserHistoryChannel,
  loadMoreFromUserHistoryChannelFailure,
  loadMoreFromUserHistoryChannelSuccess,
  loadRootChannel,
  loadRootChannelFailure,
  loadRootChannelSuccess,
  loadSubscribedChannel,
  loadSubscribedChannelFailure,
  loadSubscribedChannelSuccess,
  loadUserHistoryChannel,
  loadUserHistoryChannelFailure,
  loadUserHistoryChannelSuccess,
  setSource,
} from './newsfeed-results.actions';
import { NewsfeedResultsState } from './newsfeed-results.reducer';
import { getNextCursor, getResultsCount, getSeed } from './newsfeed-results.selectors';

import {
  NEWSFEED_SERVICE_CONFIG,
  NewsfeedServiceConfigInterface,
} from '../../config/newsfeed-service.config.interface';
import { NewsfeedService } from '../../service/data-access-newsfeed.service';
import { isNavigationStart } from '../../util/newsfeed-results.utils';

@Injectable()
export class NewsfeedResultsEffects {
  private loadNewContent$ = new BehaviorSubject<boolean>(true);

  // Basic actions

  setNewsfeedSource$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSource),
      withLatestFrom(this.loadNewContent$),
      map(([{ source }, loadNewContent]) => (loadNewContent ? loadContent({ source }) : keepContent()))
    )
  );

  loadContent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadContent),
      map(({ source }) => {
        if (source.feed === 'SUBSCRIPTION') {
          const fairId = source.fair;
          const exhibitorId = source.channel;
          return loadSubscribedChannel({ fairId, exhibitorId });
        } else if (source.feed === 'HISTORY') {
          return loadUserHistoryChannel();
        } else {
          return loadRootChannel();
        }
      })
    )
  );

  loadMoreContent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMoreContent),
      withLatestFrom(
        this.store.pipe(select(getResultsCount)),
        this.store.pipe(select(getSeed)),
        this.store.pipe(select(getNextCursor))
      ),
      map(([{ source }, from, seed, cursor]) => {
        if (source.feed === 'SUBSCRIPTION') {
          const exhibitorId = source.channel;
          const fairId = source.fair;
          return loadMoreFromSubscribedChannel({ fairId, exhibitorId, cursor });
        } else if (source.feed === 'HISTORY') {
          return loadMoreFromUserHistoryChannel({ from });
        } else {
          return loadMoreFromRootChannel({ from, seed });
        }
      })
    )
  );

  loadContentSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRootChannelSuccess, loadSubscribedChannelSuccess, loadUserHistoryChannelSuccess),
      withLatestFrom(this.store.pipe(select(getResultsCount))),
      map(([{ results }, count]) => loadContentSuccess({ results, count }))
    )
  );

  loadMoreContentSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        loadMoreFromRootChannelSuccess,
        loadMoreFromSubscribedChannelSuccess,
        loadMoreFromUserHistoryChannelSuccess
      ),
      withLatestFrom(this.store.pipe(select(getResultsCount))),
      map(([{ results }, count]) => loadMoreContentSuccess({ results, count }))
    )
  );

  // Load content

  loadRootChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadRootChannel),
      switchMap(() =>
        this.newsfeedService.getRootChannelResults().pipe(
          map((result) => loadRootChannelSuccess(result)),
          catchError((error) => of(loadRootChannelFailure({ error })))
        )
      )
    )
  );

  loadSubscribedChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSubscribedChannel),
      switchMap(({ fairId, exhibitorId }) =>
        this.store.pipe(
          select(getChannels),
          filter((channels) => channels?.length > 0),
          take(1),
          map((channels) => channels.find((channel) => channel.fairId === fairId && channel.id === exhibitorId)),
          filter((channel: Channel | undefined): channel is Channel => !!channel),
          switchMap(({ fairId, id, lastVisitedAt }) =>
            this.newsfeedService.getSubscribedChannelResults(fairId, id, undefined, lastVisitedAt).pipe(
              map((result) => loadSubscribedChannelSuccess({ ...result, fairId, exhibitorId: id })),
              catchError((error) => of(loadSubscribedChannelFailure({ error })))
            )
          )
        )
      )
    )
  );

  loadUserHistoryChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserHistoryChannel),
      switchMap(() =>
        this.store.pipe(
          select(getUserHistoryEntries),
          filter((entries) => entries?.length > 0),
          take(1),
          switchMap((entries) =>
            of(this.config.initialItemsCount).pipe(
              switchMap((count) =>
                this.newsfeedService.getUserHistoryResults(entries, 0, count).pipe(
                  map((results) => loadUserHistoryChannelSuccess({ results, count: entries.length, from: 0 })),
                  catchError((error) => of(loadUserHistoryChannelFailure({ error })))
                )
              )
            )
          )
        )
      )
    )
  );

  // Load more content

  loadMoreFromRootChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMoreFromRootChannel),
      switchMap(({ from, seed }) =>
        this.newsfeedService.getRootChannelResults(from, seed).pipe(
          map((result) => loadMoreFromRootChannelSuccess(result)),
          catchError((error) => of(loadMoreFromRootChannelFailure({ error })))
        )
      )
    )
  );

  loadMoreFromSubscribedChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMoreFromSubscribedChannel),
      withLatestFrom(this.store.pipe(select(getChannels))),
      switchMap(([{ fairId, exhibitorId, cursor }, channels]) =>
        of(exhibitorId).pipe(
          map((exhibitorId) => channels.find((channel) => channel.fairId === fairId && channel.id === exhibitorId)),
          filter((channel) => channel !== undefined),
          map((channel) => channel as Channel),
          switchMap(({ fairId, id, lastVisitedAt }) =>
            this.newsfeedService.getSubscribedChannelResults(fairId, id, cursor, lastVisitedAt).pipe(
              map(({ results, nextCursor }) =>
                loadMoreFromSubscribedChannelSuccess({ results, nextCursor, fairId, exhibitorId: id })
              ),
              catchError((error) => of(loadMoreFromSubscribedChannelFailure({ error })))
            )
          )
        )
      )
    )
  );

  loadMoreFromUserHistoryChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadMoreFromUserHistoryChannel),
      withLatestFrom(of(this.config.loadMoreItemsCount), this.store.pipe(select(getUserHistoryEntries))),
      switchMap(([{ from }, count, items]) =>
        this.newsfeedService.getUserHistoryResults(items, from, count).pipe(
          map((results) => loadMoreFromUserHistoryChannelSuccess({ results, from })),
          catchError((error) => of(loadMoreFromUserHistoryChannelFailure({ error })))
        )
      )
    )
  );

  // Side effects

  markSubscribedChannelAsVisited$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSubscribedChannelSuccess),
      map(({ fairId, exhibitorId }) => markAsVisited({ fairId, channelType: 'EXHIBITOR', id: exhibitorId }))
    )
  );

  constructor(
    private readonly actions$: Actions,
    private store: Store<NewsfeedResultsState>,
    private newsfeedService: NewsfeedService,
    private router: Router,
    @Inject(NEWSFEED_SERVICE_CONFIG) private config: NewsfeedServiceConfigInterface
  ) {
    this.router.events.pipe(filter(isNavigationStart)).subscribe((event) => {
      const navigationStartEvent = event as NavigationStart;
      const isNewsfeedTarget = navigationStartEvent.url.includes('/newsfeed');
      const isImperative = navigationStartEvent.navigationTrigger !== 'popstate';
      if (isNewsfeedTarget && isImperative) {
        this.loadNewContent$.next(true);
      } else {
        this.loadNewContent$.next(false);
      }
    });
  }
}
